import {
  Dialog,
  DialogTitle,
  Typography,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControl,
  FormControlLabel,
  Radio,
  Box,
  CircularProgress,
  Alert,
  LinearProgress,
  Checkbox,
} from "@mui/material";
import React, { useEffect } from "react";
import {
  PricingType,
  ProductType,
  SetupType,
} from "../../../../models/features/Integration/DmsSetup";
import {
  useSetPriceSyncingTypeMutation,
  useGetAvailablePriceTypesQuery,
} from "../../../../redux/reducer/features/Integration/DmsSetupSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../redux/reducer/rootReducer";
import {
  setBulkPriceSyncing,
  setPriceSynceType,
} from "../../../../redux/reducer/features/Auth/authSlice";
import {
  setupTypeLabelMap,
  usePriceOptions,
} from "../../../../services/utilities/utility";

export interface NewPriceProps {
  isOpen: boolean;
  handleClose: any;
  productType: ProductType;
}

const GridWithNewPrices: React.FunctionComponent<NewPriceProps> = ({
  handleClose,
  isOpen,
  productType,
}) => {
  const { dmsSetup } = useSelector((state: AppState) => state.authReducer);
  const dispatch = useDispatch();
  const priceOptions = usePriceOptions();
  const [priceType, setPriceType] = React.useState<PricingType>(
    PricingType.Price,
  );
  const [applyRuleConfirm, setApplyRuleConfirm] =
    React.useState<boolean>(false);
  const [availablePriceOptions, seAvailablePriceOptions] = React.useState<
    PricingType[]
  >([]);
  const [isErrorFound, setIsErrorFound] = React.useState(false);
  const [applyToAll, setApplyToAll] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");

  const { data, isLoading } = useGetAvailablePriceTypesQuery({
    producType: productType,
  });
  const [setPriceSyncingMutation, setPriceSyncingMutationResult] =
    useSetPriceSyncingTypeMutation();

  React.useEffect(() => {
    if (productType === ProductType.Wholegoods) {
      const enumValue =
        PricingType[dmsSetup?.priceType.toString() as keyof typeof PricingType];
      setPriceType(enumValue);
    } else {
      const enumValue =
        PricingType[
          dmsSetup?.partsPriceType.toString() as keyof typeof PricingType
        ];
      setPriceType(enumValue);
    }
  }, []);

  const handleApplyPriceRule = () => {
    setIsErrorFound(false);
    setErrorMessage("");
    setPriceSyncingMutation({
      pricing: priceType,
      productType: productType,
      syncAll: applyToAll,
      productIds: [],
    }).then((res: any) => {
      if (res?.data && res?.data?.status === 200) {
        dispatch(
          setPriceSynceType({
            type: PricingType[priceType],
            productType: productType,
          }),
        );
        if (applyToAll) {
          dispatch(
            setBulkPriceSyncing({ isSyncing: true, productType: productType }),
          );
        }
        setApplyRuleConfirm(false);
        handleClose();
      } else {
        setIsErrorFound(true);
        if (res?.data && res?.data?.status === 208) {
          setErrorMessage(
            "A bulk operation for price updates is already running. Please wait for a while to complete it!",
          );
        } else if (res?.data?.status === 400) {
          setErrorMessage(
            "Found error while updating prices. Please contact to the support!",
          );
        } else {
          setErrorMessage("Something went wrong. Please try again in a while!");
        }
      }
    });
  };
  const handlePriceTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPriceType(Number(event.target.value) as PricingType);
  };

  useEffect(() => {
    if (data && data?.entityList && data?.entityList?.length > 0) {
      const allowedTypeNumbers = data.entityList
        .map((name: string) => PricingType[name as keyof typeof PricingType])
        .filter((val): val is PricingType => val !== undefined);
      seAvailablePriceOptions(allowedTypeNumbers);
    }
  }, [data]);
  return (
    <>
      <Dialog
        fullWidth
        open={isOpen}
        onClose={() => {
          handleClose(false);
        }}
      >
        <DialogTitle sx={{ textAlign: "center" }}>
          <Typography color="primary" sx={{ fontWeight: "bold" }}>
            Default Price Setting
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ width: "auto" }}>
          <Typography>
            Select the price that will apply to all newly synced products.
          </Typography>
          {isLoading ? (
            <LinearProgress />
          ) : (
            <FormControl>
              <RadioGroup
                name="price-type-options"
                value={priceType}
                onChange={handlePriceTypeChange}
              >
                {priceOptions
                  .filter((x) => availablePriceOptions.includes(x.type))
                  .map((option) => (
                    <FormControlLabel
                      key={option.key}
                      value={option.type}
                      control={<Radio />}
                      label={
                        <Typography fontSize="0.9rem">
                          {option.label}
                        </Typography>
                      }
                    />
                  ))}
              </RadioGroup>
            </FormControl>
          )}
          <Box mt={1}>
            <Alert severity="warning">
              The products with zero{" "}
              {dmsSetup?.dmsLocations[0].dmsType === 0
                ? "web price"
                : "price level"}{" "}
              will have the <b>MSRP</b> by default.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
            ml: 2,
            mr: 2,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={applyToAll}
                onChange={(e) => setApplyToAll(e.target.checked)}
              />
            }
            label="Apply this pricing level to all products"
          />
          <Box display={"flex"} gap={1}>
            <Button
              size="small"
              variant="outlined"
              autoFocus
              onClick={() => {
                handleClose(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                if (applyToAll) setApplyRuleConfirm(true);
                else handleApplyPriceRule();
              }}
              disabled={setPriceSyncingMutationResult.isLoading}
            >
              {setPriceSyncingMutationResult.isLoading ? (
                <CircularProgress size="1.2rem" color="inherit" />
              ) : (
                "Save"
              )}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <Dialog
        fullWidth
        open={applyRuleConfirm}
        onClose={() => setApplyRuleConfirm(false)}
        aria-labelledby="draggable-dialog-title"
      >
        <DialogTitle
          style={{ textAlign: "center" }}
          id="draggable-dialog-title"
        >
          <Typography color="primary" sx={{ fontWeight: "bold" }}>
            Confirm to Apply
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure to apply this price to all products?
          </Typography>
          <Alert severity="info" sx={{ fontSize: "small" }}>
            Price updates on{" "}
            {setupTypeLabelMap[dmsSetup?.setupType ?? SetupType.Shopify]} may
            take 5–10 minutes, depending on the number of products.
          </Alert>
          {isErrorFound ? (
            <Alert sx={{ mt: 1 }} severity="warning">
              {errorMessage}
            </Alert>
          ) : (
            ""
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            size="small"
            autoFocus
            onClick={() => {
              setApplyRuleConfirm(false);
            }}
          >
            No
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={setPriceSyncingMutationResult.isLoading}
            onClick={handleApplyPriceRule}
          >
            {setPriceSyncingMutationResult.isLoading ? (
              <CircularProgress size="1.2rem" color="inherit" />
            ) : (
              "Yes"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GridWithNewPrices;
